import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Server, Plus, Trash2, Pencil, Loader2, Link2, Clock, Activity } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const DURATION_OPTIONS = [24, 48, 72, 168];
const emptyForm = { name: "", description: "", allowed_test_durations: [24], enabled: true, plan_costs: {} };

function DurationPicker({ value, onChange, testid }) {
  const toggle = (d) => {
    onChange(value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort((a, b) => a - b));
  };
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {DURATION_OPTIONS.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => toggle(d)}
          data-testid={`${testid}-${d}`}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors duration-200 ${
            value.includes(d)
              ? "bg-primary text-primary-foreground border-primary"
              : "border-border text-muted-foreground hover:border-primary/50"
          }`}
        >
          {d >= 168 ? `${d / 24}d` : `${d}h`}
        </button>
      ))}
    </div>
  );
}

export default function Panels() {
  const [panels, setPanels] = useState(null);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(null);

  const checkHealth = async (p) => {
    setChecking(p.id);
    try {
      const { data } = await api.post(`/panels/${p.id}/health-check`);
      if (data.status === "online") toast.success(`${p.name} is online (${data.reachable}/${data.checked} links reachable)`);
      else if (data.status === "no-links") toast.info(`${p.name} has no links to check`);
      else toast.warning(`${p.name} is offline`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setChecking(null); }
  };

  const statusBadge = (p) => {
    const map = {
      online: "border-success/40 text-success",
      offline: "border-destructive/40 text-destructive",
      "no-links": "border-border text-muted-foreground",
      unknown: "border-border text-muted-foreground",
    };
    return <Badge variant="outline" className={map[p.status] || map.unknown} data-testid={`panel-status-${p.id}`}>{p.status || "unknown"}</Badge>;
  };

  const load = async () => {
    try { const { data } = await api.get("/panels"); setPanels(data); }
    catch { toast.error("Failed to load panels"); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/plans").then(({ data }) => setPlans(data)).catch(() => {}); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || "", allowed_test_durations: p.allowed_test_durations, enabled: p.enabled, plan_costs: p.plan_costs || {} }); setOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) { await api.put(`/panels/${editing.id}`, form); toast.success("Panel updated"); }
      else { await api.post("/panels", form); toast.success("Panel added"); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/panels/${p.id}`); toast.success("Panel deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
  };

  const toggleEnabled = async (p) => {
    try { await api.put(`/panels/${p.id}`, { enabled: !p.enabled }); load(); }
    catch { toast.error("Failed to update"); }
  };

  return (
    <div data-testid="panels-page">
      <PageHeader overline="Infrastructure" title="Panels" description="Logical IPTV providers — no API required. Define allowed test durations per panel.">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="add-panel-button"><Plus className="h-4 w-4 mr-2" /> Add Panel</Button>
          </DialogTrigger>
          <DialogContent className="bg-popover sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit Panel" : "Add Panel"}</DialogTitle>
              <DialogDescription>A panel is a logical provider (e.g. XSUI, TVPlus) that groups links and templates.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2"><Label>Panel name</Label><Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="XSUI" data-testid="panel-name" /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={2} data-testid="panel-desc" /></div>
              <div className="space-y-2">
                <Label>Allowed test durations</Label>
                <DurationPicker value={form.allowed_test_durations} onChange={(v) => setForm((f) => ({ ...f, allowed_test_durations: v }))} testid="panel-durations" />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
                <Label className="cursor-pointer">Enabled</Label>
                <Switch checked={form.enabled} onCheckedChange={(v) => setForm((f) => ({ ...f, enabled: v }))} data-testid="panel-enabled" />
              </div>
              {plans.filter((p) => !p.is_trial).length > 0 && (
                <div className="space-y-2">
                  <Label>Plan cost overrides <span className="text-muted-foreground font-normal">(credits — leave empty to use the plan default)</span></Label>
                  <div className="space-y-2 rounded-md border border-border p-3" data-testid="panel-plan-costs">
                    {plans.filter((p) => !p.is_trial).map((p) => (
                      <div key={p.id} className="flex items-center justify-between gap-3">
                        <span className="text-sm truncate">{p.name} <span className="text-xs text-muted-foreground">({p.credits} cr default)</span></span>
                        <Input type="number" min="0" className="w-24 h-8" placeholder={`${p.credits}`}
                          value={form.plan_costs?.[p.id] ?? ""}
                          onChange={(e) => setForm((f) => {
                            const pc = { ...(f.plan_costs || {}) };
                            if (e.target.value === "") delete pc[p.id];
                            else pc[p.id] = Number(e.target.value);
                            return { ...f, plan_costs: pc };
                          })}
                          data-testid={`panel-plancost-${p.id}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving || !form.name || form.allowed_test_durations.length === 0} data-testid="panel-save-button">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} {editing ? "Save" : "Add Panel"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      {!panels ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-lg" />)}
        </div>
      ) : panels.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <Server className="h-12 w-12 mx-auto text-muted-foreground" />
          <p className="mt-4 font-display text-lg font-semibold">No panels yet</p>
          <Button className="mt-5" onClick={openCreate}><Plus className="h-4 w-4 mr-2" /> Add Panel</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {panels.map((p) => (
            <Card key={p.id} className="p-5 border-border bg-card transition-transform duration-200 hover:-translate-y-0.5" data-testid={`panel-card-${p.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="grid place-items-center h-10 w-10 rounded-md bg-accent/10 text-accent"><Server className="h-5 w-5" /></div>
                  <div>
                    <p className="font-display font-semibold">{p.name}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1"><Link2 className="h-3 w-3" /> {p.link_count} links</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Badge variant="outline" className={p.enabled ? "border-success/40 text-success" : "border-border text-muted-foreground"}>{p.enabled ? "Enabled" : "Disabled"}</Badge>
                  {statusBadge(p)}
                </div>
              </div>
              {p.description && <p className="text-sm text-muted-foreground mt-3">{p.description}</p>}
              <div className="mt-4">
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1"><Clock className="h-3 w-3" /> Test durations</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.allowed_test_durations.map((d) => (
                    <Badge key={d} variant="secondary" className="font-mono">{d >= 168 ? `${d / 24}d` : `${d}h`}</Badge>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <div className="flex items-center gap-2">
                  <Switch checked={p.enabled} onCheckedChange={() => toggleEnabled(p)} data-testid={`panel-toggle-${p.id}`} />
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => checkHealth(p)} disabled={checking === p.id} title="Check connection" data-testid={`panel-health-${p.id}`}>
                    {checking === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`panel-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`panel-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-popover">
                      <AlertDialogHeader><AlertDialogTitle>Delete {p.name}?</AlertDialogTitle>
                        <AlertDialogDescription>Panels with links in the pool cannot be deleted.</AlertDialogDescription></AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => remove(p)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
